import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ArrowRight, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const STEPS = ["Account", "Student", "Property", "Plan & Add-ons", "Schedule", "Pay"];

export default function Booking() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") || "biweekly";

  const [step, setStep] = useState(user ? 1 : 0);
  const [catalog, setCatalog] = useState({ plans: {}, addons: {} });
  const [students, setStudents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);

  // step data
  const [studentForm, setStudentForm] = useState({ name: "", email: "", phone: "", school: "" });
  const [studentId, setStudentId] = useState("");
  const [propertyForm, setPropertyForm] = useState({
    name: "", type: "apartment", address: "", building: "", unit: "", room_number: "", door_code: "", access_notes: "",
  });
  const [propertyId, setPropertyId] = useState("");
  const [plan, setPlan] = useState(initialPlan);
  const [addons, setAddons] = useState([]);
  const [startDate, setStartDate] = useState(undefined);
  const [notes, setNotes] = useState("");

  useEffect(() => { if (!loading && user) setStep((s) => Math.max(s, 1)); }, [loading, user]);

  useEffect(() => {
    http.get("/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      http.get("/students").then(({ data }) => setStudents(data || [])).catch(() => {});
      http.get("/properties").then(({ data }) => setProperties(data || [])).catch(() => {});
    }
  }, [user]);

  const planMeta = catalog.plans?.[plan];
  const total = (planMeta?.price || 0) + addons.reduce((s, k) => s + (catalog.addons?.[k]?.price || 0), 0);

  function toggleAddon(k) {
    setAddons((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);
  }

  async function createStudent() {
    setBusy(true);
    try {
      const { data } = await http.post("/students", studentForm);
      setStudents((s) => [...s, data]);
      setStudentId(data.id);
      setStep(2);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function createProperty() {
    setBusy(true);
    try {
      const { data } = await http.post("/properties", { ...propertyForm, student_id: studentId });
      setProperties((p) => [...p, data]);
      setPropertyId(data.id);
      setStep(3);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function startCheckout() {
    if (!startDate) { toast.error("Please choose a start date"); return; }
    setBusy(true);
    try {
      const isRecurring = planMeta?.recurring;
      if (isRecurring) {
        // Real subscription path
        const { data: session } = await http.post("/subscriptions/checkout", {
          student_id: studentId,
          placement_id: propertyId,
          service_code: plan,
          start_date: format(startDate, "yyyy-MM-dd"),
          notes,
          origin_url: window.location.origin,
        });
        window.location.href = session.url;
        return;
      }
      // One-time booking path (legacy)
      const { data: booking } = await http.post("/bookings", {
        student_id: studentId,
        property_id: propertyId,
        plan,
        addons,
        start_date: format(startDate, "yyyy-MM-dd"),
        notes,
      });
      const { data: session } = await http.post("/checkout/session", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = session.url;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  }

  // STEP 0: must sign in
  if (!user && !loading) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">Sign in to book</h1>
        <p className="mt-2 text-muted-foreground">Booking lives inside your parent account so we can manage your recurring schedule.</p>
        <div className="mt-6 flex gap-2 justify-center">
          <Button onClick={() => navigate("/login", { state: { from: { pathname: "/booking" } } })} data-testid="book-go-login">Sign in</Button>
          <Button variant="outline" onClick={() => navigate("/register")} data-testid="book-go-register">Create account</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Badge variant="outline" className="mb-3">Booking</Badge>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary">Set up your cleaning</h1>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 mt-6 mb-8">
        {STEPS.slice(1).map((s, i) => {
          const idx = i + 1;
          const active = idx === step;
          const done = idx < step;
          return (
            <div key={s} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? "bg-primary text-primary-foreground border-primary" : done ? "bg-secondary/10 text-secondary border-secondary/20" : "bg-muted text-muted-foreground border-border"}`}>
              {done && <Check size={12} className="inline mr-1" />}
              {idx}. {s}
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/60">
          <CardContent className="p-6 space-y-5">
            {/* STUDENT */}
            {step === 1 && (
              <>
                <h2 className="font-display text-xl font-semibold text-primary">Who's the student?</h2>
                {students.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Use existing</Label>
                    <Select value={studentId} onValueChange={(v) => setStudentId(v)}>
                      <SelectTrigger data-testid="select-existing-student"><SelectValue placeholder="Pick a student..." /></SelectTrigger>
                      <SelectContent>
                        {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="border-t pt-4">
                  <div className="text-sm text-muted-foreground mb-3">…or add a new student</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Student's name" value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} data-testid="student-name" />
                    <Input placeholder="College / school" value={studentForm.school} onChange={(e) => setStudentForm({ ...studentForm, school: e.target.value })} data-testid="student-school" />
                    <Input placeholder="Email (optional)" value={studentForm.email} onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })} data-testid="student-email" />
                    <Input placeholder="Phone (optional)" value={studentForm.phone} onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })} data-testid="student-phone" />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => studentId ? setStep(2) : createStudent()} disabled={busy || (!studentId && !studentForm.name)} className="bg-primary hover:bg-primary/90" data-testid="booking-step1-next">
                    Continue <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </>
            )}

            {/* PROPERTY */}
            {step === 2 && (
              <>
                <h2 className="font-display text-xl font-semibold text-primary">Where do they live?</h2>
                {properties.filter((p) => p.student_id === studentId).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Use existing</Label>
                    <Select value={propertyId} onValueChange={(v) => setPropertyId(v)}>
                      <SelectTrigger data-testid="select-existing-property"><SelectValue placeholder="Pick a property..." /></SelectTrigger>
                      <SelectContent>
                        {properties.filter((p) => p.student_id === studentId).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="border-t pt-4">
                  <div className="text-sm text-muted-foreground mb-3">…or add a new property</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Nickname (e.g. Sarah's dorm)" value={propertyForm.name} onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })} data-testid="prop-name" />
                    <Select value={propertyForm.type} onValueChange={(v) => setPropertyForm({ ...propertyForm, type: v })}>
                      <SelectTrigger data-testid="prop-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dorm">Dorm room</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="leased_bedroom">Leased bedroom</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="sm:col-span-2" placeholder="Street address" value={propertyForm.address} onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })} data-testid="prop-address" />
                    <Input placeholder="Building (optional)" value={propertyForm.building} onChange={(e) => setPropertyForm({ ...propertyForm, building: e.target.value })} data-testid="prop-building" />
                    <Input placeholder="Unit (optional)" value={propertyForm.unit} onChange={(e) => setPropertyForm({ ...propertyForm, unit: e.target.value })} data-testid="prop-unit" />
                    <Input placeholder="Room # (optional)" value={propertyForm.room_number} onChange={(e) => setPropertyForm({ ...propertyForm, room_number: e.target.value })} data-testid="prop-room" />
                    <Input placeholder="Door code (optional)" value={propertyForm.door_code} onChange={(e) => setPropertyForm({ ...propertyForm, door_code: e.target.value })} data-testid="prop-door" />
                  </div>
                  <Textarea className="mt-3" placeholder="Access notes — gate codes, lockbox, etc." value={propertyForm.access_notes} onChange={(e) => setPropertyForm({ ...propertyForm, access_notes: e.target.value })} data-testid="prop-notes" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button onClick={() => propertyId ? setStep(3) : createProperty()} disabled={busy || (!propertyId && (!propertyForm.name || !propertyForm.address))} className="bg-primary hover:bg-primary/90" data-testid="booking-step2-next">
                    Continue <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </>
            )}

            {/* PLAN */}
            {step === 3 && (
              <>
                <h2 className="font-display text-xl font-semibold text-primary">Choose a plan</h2>
                <RadioGroup value={plan} onValueChange={setPlan} className="space-y-2">
                  {Object.entries(catalog.plans || {}).map(([k, p]) => (
                    <label key={k} htmlFor={`plan-${k}`} className={`flex items-center justify-between border rounded-xl p-4 cursor-pointer ${plan === k ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`plan-radio-${k}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem id={`plan-${k}`} value={k} />
                        <div>
                          <div className="font-semibold text-primary">{p.label}</div>
                          <div className="text-xs text-muted-foreground">{p.recurring ? "Recurring" : "One-time"}</div>
                        </div>
                      </div>
                      <div className="font-display font-bold text-primary">${p.price}</div>
                    </label>
                  ))}
                </RadioGroup>

                <h3 className="font-display font-semibold text-primary mt-6">Add-ons (optional)</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(catalog.addons || {}).map(([k, a]) => (
                    <label key={k} htmlFor={`addon-${k}`} className={`flex items-center justify-between border rounded-xl p-3 cursor-pointer ${addons.includes(k) ? "border-secondary bg-secondary/5" : "border-border"}`} data-testid={`addon-${k}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox id={`addon-${k}`} checked={addons.includes(k)} onCheckedChange={() => toggleAddon(k)} />
                        <span className="text-sm text-primary">{a.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-secondary">+${a.price}</span>
                    </label>
                  ))}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                  <Button onClick={() => setStep(4)} className="bg-primary hover:bg-primary/90" data-testid="booking-step3-next">Continue</Button>
                </div>
              </>
            )}

            {/* SCHEDULE */}
            {step === 4 && (
              <>
                <h2 className="font-display text-xl font-semibold text-primary">When should we start?</h2>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">First cleaning date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="schedule-date-trigger">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={startDate} onSelect={setStartDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Special instructions for the cleaner</Label>
                  <Textarea placeholder="e.g. quiet hours, pet, allergies, etc." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="schedule-notes" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                  <Button onClick={() => setStep(5)} disabled={!startDate} className="bg-primary hover:bg-primary/90" data-testid="booking-step4-next">Review & pay</Button>
                </div>
              </>
            )}

            {/* PAY */}
            {step === 5 && (
              <>
                <h2 className="font-display text-xl font-semibold text-primary">Review & pay</h2>
                <div className="border rounded-xl p-4 bg-muted/30 space-y-2 text-sm">
                  <div className="flex justify-between"><span>{planMeta?.label}</span><b>${planMeta?.price}</b></div>
                  {addons.map((k) => (
                    <div key={k} className="flex justify-between text-muted-foreground"><span>{catalog.addons[k]?.label}</span><span>+${catalog.addons[k]?.price}</span></div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-display text-lg"><span>Today's total</span><b className="text-primary">${total.toFixed(2)}</b></div>
                  {planMeta?.recurring && <div className="text-xs text-muted-foreground">After payment, this becomes a recurring schedule. Cancel any time.</div>}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(4)} disabled={busy}>Back</Button>
                  <Button onClick={startCheckout} disabled={busy} className="bg-secondary hover:bg-secondary/90" data-testid="booking-pay-btn">
                    {busy ? "Redirecting…" : `Pay $${total.toFixed(2)} with Stripe`}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SUMMARY SIDE */}
        <Card className="border-border/60 h-fit lg:sticky lg:top-24">
          <CardContent className="p-6 space-y-3 text-sm">
            <div className="font-display font-semibold text-primary">Summary</div>
            <Row label="Student" value={students.find((s) => s.id === studentId)?.name || "—"} />
            <Row label="Property" value={properties.find((p) => p.id === propertyId)?.name || propertyForm.name || "—"} />
            <Row label="Plan" value={planMeta?.label || "—"} />
            <Row label="Add-ons" value={addons.length ? addons.map((k) => catalog.addons[k]?.label).join(", ") : "None"} />
            <Row label="Start date" value={startDate ? format(startDate, "PP") : "—"} />
            <div className="border-t pt-3 flex justify-between font-display"><span>Total</span><b className="text-primary text-lg">${total.toFixed(2)}</b></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-primary font-medium">{value}</span>
    </div>
  );
}
